// Estado de sesión de la plataforma nueva (login con contraseña o Google).
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  accessTokenNeedsRefresh,
  api,
  apiErrorCode,
  getAccessToken,
  refreshAccessToken,
  setAccessToken,
  setSessionLostHandler,
  tokenClinicId,
} from './api';

export interface PnUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  default_role: string;
}

export interface PnClinic {
  id: string;
  slug: string;
  name: string;
  is_demo: boolean;
}

export interface PnClinicSummary {
  id: string;
  slug: string;
  name: string;
  role: string;
}

interface LoginResponse {
  access_token: string;
  expires_in: number;
  user: PnUser;
  clinic: PnClinic | null;
  clinics_available?: PnClinicSummary[];
}

interface SwitchClinicResponse {
  access_token: string;
  expires_in: number;
  clinic: PnClinic | null;
  clinics_available?: PnClinicSummary[];
}

interface MeResponse {
  user: PnUser;
  clinic?: PnClinic | null;
  current_clinic?: PnClinic | null;
  clinics_available?: PnClinicSummary[];
}

export type GoogleOutcome =
  | { ok: true }
  | { ok: false; needsPasswordLink: true }
  | { ok: false; needsPasswordLink?: false; error: string };

/** Canal entre pestañas: al salir en una, salen todas (cierre real). */
export const SESSION_CHANNEL = 'wiservet-pn-session';
/** Evento de ventana cuando la sesión se pierde (lo escucha el router). */
export const SESSION_LOST_EVENT = 'wiservet-pn:session-lost';
/**
 * Evento de ventana cuando OTRA pestaña cambió de clínica: la sesión (cookie) ya
 * apunta a la clínica nueva, así que esta pestaña recarga desde Inicio (lo escucha
 * el router) para no mezclar el nombre de una clínica con datos de otra.
 */
export const CLINIC_CHANGED_ELSEWHERE_EVENT = 'wiservet-pn:clinic-changed-elsewhere';

function openChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(SESSION_CHANNEL) : null;
}

/** Evita que Google vuelva a entrar solo con la última cuenta tras salir. */
function disableGoogleAutoSelect(): void {
  const g = (globalThis as { google?: { accounts?: { id?: { disableAutoSelect?: () => void } } } }).google;
  try {
    g?.accounts?.id?.disableAutoSelect?.();
  } catch {
    // GIS no cargado: nada que hacer.
  }
}

export const useSessionStore = defineStore('pn-session', () => {
  const user = ref<PnUser | null>(null);
  const clinic = ref<PnClinic | null>(null);
  const clinicsAvailable = ref<PnClinicSummary[]>([]);
  const ready = ref(false);
  const error = ref<string | null>(null);
  const switching = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  const channel = openChannel();

  function clearLocal(): void {
    user.value = null;
    clinic.value = null;
    clinicsAvailable.value = [];
    setAccessToken(null);
  }

  async function applyLogin(data: LoginResponse): Promise<void> {
    setAccessToken(data.access_token, data.expires_in);
    user.value = data.user;
    clinic.value = data.clinic ?? null;
    clinicsAvailable.value = data.clinics_available ?? [];
    // Usuario con varias clínicas sin elegir: /auth/me resuelve la principal.
    if (!clinic.value) await fetchMe();
    await anclarClinica();
    ready.value = true;
  }

  /**
   * Con 2 o más clínicas el login no elige ninguna: el token llega sin clinic_id
   * (los datos salen vacíos: la RLS falla cerrado) aunque /auth/me muestre una
   * clínica. Aquí se ancla el token a la clínica que se muestra, con el mismo
   * cambio de clínica del selector (el backend valida la membresía y guarda la
   * clínica en la sesión, así la renovación siguiente ya la trae).
   */
  async function anclarClinica(): Promise<void> {
    const mostrada = clinic.value?.id;
    if (!mostrada || tokenClinicId(getAccessToken()) === mostrada) return;
    await cambiar(mostrada, false);
  }

  async function cambiar(clinicId: string, avisarPestanas: boolean): Promise<boolean> {
    try {
      const { data } = await api.post<SwitchClinicResponse>('/auth/switch-clinic', { clinic_id: clinicId });
      setAccessToken(data.access_token, data.expires_in);
      clinic.value = data.clinic ?? null;
      if (data.clinics_available) clinicsAvailable.value = data.clinics_available;
      if (avisarPestanas) channel?.postMessage({ type: 'clinic', clinicId });
      return true;
    } catch (e) {
      const err = apiErrorCode(e);
      error.value =
        err.code === 'CLINIC_NOT_FOUND'
          ? 'No tienes acceso a esa clínica'
          : err.code === 'NETWORK_ERROR'
            ? 'Sin conexión. Intenta de nuevo.'
            : 'No se pudo cambiar de clínica. Intenta de nuevo.';
      return false;
    }
  }

  /**
   * Selector de clínica del menú: pide al backend un token de la clínica elegida
   * (valida que el usuario sea miembro; si no, 404) y avisa a las otras pestañas.
   */
  async function switchClinic(clinicId: string): Promise<boolean> {
    if (switching.value) return false;
    if (clinicId === clinic.value?.id) return true;
    error.value = null;
    switching.value = true;
    try {
      return await cambiar(clinicId, true);
    } finally {
      switching.value = false;
    }
  }

  async function fetchMe(): Promise<boolean> {
    try {
      const { data } = await api.get<MeResponse>('/auth/me');
      user.value = data.user;
      clinic.value = data.clinic ?? data.current_clinic ?? null;
      clinicsAvailable.value = data.clinics_available ?? [];
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Arranque: si no hay access token en memoria (pestaña nueva, navegador
   * reabierto), renueva en silencio con la cookie antes de decidir si hay sesión.
   */
  async function bootstrap(): Promise<void> {
    if (ready.value) return;
    if (!getAccessToken()) await refreshAccessToken();
    if (getAccessToken()) {
      if (await fetchMe()) await anclarClinica();
      else clearLocal();
    }
    ready.value = true;
  }

  async function login(email: string, password: string): Promise<boolean> {
    error.value = null;
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
      await applyLogin(data);
      return true;
    } catch (e) {
      const err = apiErrorCode(e);
      error.value =
        err.code === 'INVALID_CREDENTIALS'
          ? 'Correo o contraseña incorrectos'
          : err.code === 'USER_LOCKED'
            ? 'Demasiados intentos, intenta más tarde'
            : err.message;
      return false;
    }
  }

  async function googleNonce(): Promise<string> {
    const { data } = await api.get<{ nonce: string }>('/auth/google/nonce');
    return data.nonce;
  }

  async function googleLogin(idToken: string): Promise<GoogleOutcome> {
    error.value = null;
    try {
      const { data } = await api.post<LoginResponse>('/auth/google', { id_token: idToken });
      await applyLogin(data);
      return { ok: true };
    } catch (e) {
      const err = apiErrorCode(e);
      if (err.code === 'NEEDS_PASSWORD_LINK') return { ok: false, needsPasswordLink: true };
      error.value =
        err.code === 'INVALID_CREDENTIALS' ? 'No encontramos una cuenta activa con ese correo de Google' : err.message;
      return { ok: false, error: error.value };
    }
  }

  async function linkGoogleWithPassword(idToken: string, password: string): Promise<boolean> {
    error.value = null;
    try {
      const { data } = await api.post<LoginResponse>('/auth/google/link-with-password', {
        id_token: idToken,
        password,
      });
      await applyLogin(data);
      return true;
    } catch (e) {
      const err = apiErrorCode(e);
      error.value = err.code === 'INVALID_CREDENTIALS' ? 'Contraseña incorrecta' : err.message;
      return false;
    }
  }

  /**
   * Cierre real: el backend revoca la sesión de la cookie (ya no puede
   * renovarse), se borra el estado local, Google deja de autoseleccionar la
   * cuenta y las demás pestañas también salen.
   */
  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Igual se limpia lo local: la cookie la borra el backend o vence.
    }
    clearLocal();
    disableGoogleAutoSelect();
    channel?.postMessage({ type: 'logout' });
  }

  /** Al volver a la pestaña tras horas, renovar antes del primer clic. */
  async function onVisible(): Promise<void> {
    if (!isAuthenticated.value || !accessTokenNeedsRefresh()) return;
    if (!(await refreshAccessToken())) {
      clearLocal();
      window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT));
    }
  }

  setSessionLostHandler(() => {
    user.value = null;
    clinic.value = null;
    clinicsAvailable.value = [];
    window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT));
  });

  if (channel) {
    channel.onmessage = (ev: MessageEvent<{ type?: string }>) => {
      if (ev.data?.type === 'logout') {
        clearLocal();
        disableGoogleAutoSelect();
        window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT));
      } else if (ev.data?.type === 'clinic' && isAuthenticated.value) {
        window.dispatchEvent(new CustomEvent(CLINIC_CHANGED_ELSEWHERE_EVENT));
      }
    };
  }

  return {
    user,
    clinic,
    clinicsAvailable,
    ready,
    error,
    switching,
    isAuthenticated,
    bootstrap,
    fetchMe,
    switchClinic,
    login,
    googleNonce,
    googleLogin,
    linkGoogleWithPassword,
    logout,
    onVisible,
    clearLocal,
  };
});
